/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the Authentication using simulation process
 * The URI keyword would have to match something like '/x/simulation'
 * It will show a form allowing the user to sign in as cabinet's administrator
 * and simulates one of it's customers authentication
 */

// Imports
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

// Utils
import { ApiCallConstructor, GlobalApiCall, HTTPResponse, fetchDossiers } from '../../utils/ApiCallConstructor';
import { useIsMobileScreen } from '../../utils/MobileScreenContext';

// Interfaces
import { PublicCabinet } from '../../../interfaces/ICabinets';

// Buroclic adaptive components
import { PoweredByBc } from '../../buroclic/PoweredByBc';
import { ErrorForUsr, displayAlert, signOut } from '../../utils/Errors';
import { AlertType } from '../../../interfaces/IUtils';

export const SimulationLoginPage: React.FC = () => {
  document.title = 'Votre espace client';
  const isMobileScreen = useIsMobileScreen();

  // get parameters from URL
  const params = useParams();

  // Store cabinetAlias in localStorage
  const cabinetAlias = params.cabinetAlias;
  if (cabinetAlias) localStorage.setItem('cabinetAlias', cabinetAlias);

  const [cabinet, setCabinet] = useState<PublicCabinet>({} as PublicCabinet);
  const [cabinetError, setCabinetError] = useState('');


  // Get initial data
  useEffect(() => {
    // Prevent from keeping data from previous connection
    localStorage.clear();

    // Fetch cabinet Back-end data such as Host
    const fetchCabinet = async () => {
      try {
        const cabinetResponse = await toast.promise(
          GlobalApiCall({
            endpoint: `/cabinets/${cabinetAlias}`,
            method: 'GET',
          }),
          {
            pending: 'Liaison avec le cabinet en cours ...',
            error: { render({ data }) { return `Impossible d'établir une laision avec le cabinet : ${(data as HTTPResponse).error}` } },
          });

        // Handle error
        if (cabinetResponse.error !== null)
          throw new Error(cabinetResponse.error);

        const tmpCabinet = (cabinetResponse?.result as PublicCabinet);
        if (!(tmpCabinet.loginBackground)) tmpCabinet.loginBackground = '/images/background.png';
        setCabinet(tmpCabinet);
        localStorage.setItem('cabinet', JSON.stringify(tmpCabinet));
        document.title = cabinetAlias ? cabinetAlias + ' | Espace client' : 'Votre espace client';
      } catch (err: any) {
        localStorage.clear();
        console.error(err.error)
        setCabinetError(err.error);
      }
    }

    // load cabinet back-end API
    fetchCabinet();
  }, [cabinetAlias]);


  // form fields
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [isPwdShown, setIsPwdShown] = useState<boolean>(false);
  const [customerEmail, setCustomerEmail] = useState('');

  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();



  // Defines the code to be executed when the form is submit
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await signIn();
  };


  // Defines the validator for email field
  const validateEmail = (input: string) => {
    const emailPattern = /^[a-zA-Z0-9-_]+(?:\.[a-zA-Z0-9-_]+)*@[a-zA-Z0-9-_]+(?:\.[a-zA-Z0-9]+)*$/i;
    return emailPattern.test(input);
  };



  /**
   * Tries to connect the user through the Back-End
   * using credentials he/she just gave us
   */
  const signIn = async () => {

    // Check for form fields
    if (!validateEmail(customerEmail) || !login || !password)
      setErrorMessage('Veuillez renseigner l\'intégralité de vos éléments de connexion');
    else {
      try {
        // API Call
        const signInResponse: HTTPResponse = await toast.promise(
          ApiCallConstructor({
            endpoint: '/admin/signin',
            method: 'POST',
            body: {
              login: login,
              password: password,
              customerEmail: customerEmail,
              dbAlias: cabinet.dbName
            }
          }),
          {
            pending: 'Connexion en cours',
            error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } }
          });

        // Connected successfully
        window.localStorage.setItem('isAuthenticated', "true");
        window.localStorage.setItem('simulation', "true");
        window.localStorage.setItem('login', (signInResponse.result as any).login);
        window.localStorage.setItem('eclusrRef', (signInResponse.result as any).userID);

        // Store buroSecure data from back
        if ((signInResponse.result as any).buroSecure)
          window.localStorage.setItem('buroSecure', JSON.stringify((signInResponse.result as any).buroSecure));

        // Get dossiers the user has the right to see
        // and navigate to the proper endpoint based on its number of dossiers
        fetchDossiers().then((fetchedDossiers) => {
          if (fetchedDossiers.length <= 0) navigate('/accueil', { state: { error: 'Aucun document n\'a encore été publié sur votre espace client.' } }); // no dossier or document
          else navigate('/accueil'); // redirect to the home page with folders picker
        })
          .catch((err: any) => {
            displayAlert({ message: err.error, type: AlertType.error });
            if (err.error === ErrorForUsr.NotSignedIn)
              signOut((cabAlias) => navigate(`/${cabAlias}/login`));
          });

      } catch (err: any) { console.error(err.error) }
    }
  };


  useEffect(() => {
    setErrorMessage('');
  }, [login, password, customerEmail]);

  // HTML Templates
  const renderMobileForm = () => {
    return (
      <div className={`justify-center flex flex-col rounded-sm m-4`} style={{ width: 'calc(100vw - 3rem)' }} >

        {/* Error if Global Back-End has not found a record matching */}
        <div className={`${cabinetError !== '' ? 'bg-white p-4 flex flex-col' : 'hidden'}`}>
          <FontAwesomeIcon className={`m-auto w-[40%] h-[40%] pt-4 text-red-500`} icon={faCircleExclamation} />
          <div className='text-center border border-black rounded-sm mt-8'>
            <div className={`m-auto grow mt-4 p-4 mx-2 text-center`}>
              <div className='text-5xl font-bold'>{cabinetError}</div><br />
              <div className='text-3xl mt-4 m-auto'>Nous vous invitons à vérifier que l'URL de connexion utilisé correspond bien à celui disponible sur le site internet du cabinet.</div>
            </div>
          </div>
        </div>


        {/* Login form if cabinet has been found on Global Back-End */}
        <img src={cabinet.logo} alt="Company logo" className={`h-auto w-full object-contain object-center max-h-96 my-[4vh] mx-auto max-w-[75%] ${cabinetError !== '' || cabinet.logo === '' ? 'hidden' : ''}`} />
        <span className='text-center font-bold text-red-500 text-5xl mb-8'>Simulation de connexion</span>

        <form onSubmit={handleFormSubmit} className={` bg-[#f7f7f7] border border-black rounded-md p-12 pb-0 px-3 pt-2 w-auto grow ${cabinetError !== '' ? 'hidden' : ''}`}>
          <div className='flex flex-col h-full'>
            <div className='flex flex-col items-start grow'>
              <img src="/images/connexionIcon.png" alt="User icon" className="mx-auto mb-8 w-1/5" />

              {/*<!-- Login -->*/}
              <label htmlFor="login" className="text-gray-500 text-4xl mb-3 font-bold inline-block">Login administrateur :</label>
              <input type="text" id="login" placeholder='Login' value={login} onChange={(e) => setLogin(e.target.value)}
                className="border border-gray-300 bg-white py-2 px-4 text-4xl rounded-sm h-24 mb-8 shadow-bottom" style={{ width: '-webkit-fill-available' }} />

              {/*<!-- Password -->*/}
              <label htmlFor="password" className="text-gray-500 text-4xl mb-3 font-bold inline-block">Mot de passe :</label>
              <div className="border mb-8 flex items-center rounded-md w-full">
                <input type={`${isPwdShown ? 'text' : 'password'}`} id="password" placeholder='Mot de passe' value={password} onChange={(e) => setPassword(e.target.value)}
                  className="bg-white py-2 px-4 text-4xl rounded-sm h-24 shadow-bottom  w-[inherit]" />
                <button title='Voir le mot de passe' onClick={() => setIsPwdShown(!isPwdShown)} className="py-3 border-l border-l-gray-300 h-full cursor-pointer">
                  <FontAwesomeIcon className={`h-16 w-16 mx-4`} icon={isPwdShown ? faEyeSlash : faEye} />
                </button>
              </div>

              {/*<!-- Email -->*/}
              <label htmlFor="email" className="text-gray-500 text-4xl mb-3 font-bold inline-block">Email du client :</label>
              <input type="email" id="email" placeholder='Email' value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)}
                className={`border border-gray-300 bg-white py-2 px-4 text-4xl rounded-sm h-24 mb-8 ${validateEmail(customerEmail) ? '' : 'border-red-500'} shadow-bottom`}
                title="Veuillez entrer une adresse email valide." style={{ width: '-webkit-fill-available' }} />

              {errorMessage && <p className="text-red-500 mx-auto font-bold text-4xl">{errorMessage}</p>}

              <button className={`text-white mt-4 text-5xl font-bold py-8 px-12 w-4/5 mb-8 rounded-lg shadow-md mx-auto`} style={{ backgroundColor: cabinet.color }} type="submit">
                Connexion
              </button>
            </div>

            <PoweredByBc className={'text-center py-1'} imgClassName={'h-full w-auto m-auto mb-16'} />
          </div>
        </form>
      </div>
    );
  }

  const renderComputerForm = () => {
    return (
      <div className={`hidden h-full lg:flex items-center justify-center bg-contain bg-right-bottom bg-no-repeat`} style={{ backgroundImage: `url('${cabinet.loginBackground ? cabinet.loginBackground : ''}')` }}>
        <div className={`w-4/5 justify-center flex flex-col bg-[#f7f7f7] rounded-lg lg:w-1/3 min-h-[40%] h-[40%] lg:mr-auto lg:ml-12 lg:h-[min-content] lg:my-auto`} >

          {/* Error if Global Back-End has not found a record matching */}
          <div className={`${cabinetError !== '' ? 'bg-white p-4 flex flex-col' : 'hidden'}`}>
            <div className='text-center border border-black rounded-sm'>
              <FontAwesomeIcon className={`m-auto w-[20%] h-[20%] pt-4 text-red-500`} icon={faCircleExclamation} />
              <div className={`m-auto grow mt-4 mx-2 text-center`}>
                <div className='text-2xl font-bold'>{cabinetError}</div><br />
                <div className='text-xl w-[80%] m-auto'>Vérifiez que l'URL de connexion est bien celui que nous vous avions envoyé par mail initialement.</div>
              </div>
            </div>
          </div>


          {/* Login form if cabinet has been found on Global Back-End */}
          {/*<img src={cabinet.logo} alt="Company logo" className={`w-48 lg:w-64 mx-auto my-4 overflow-auto ${cabinetError !== '' || cabinet.logo === '' ? 'hidden' : ''}`} />*/}
          <img src={cabinet.logo} alt="Company logo" className={`h-auto w-full object-contain object-center max-h-[16rem] mt-[4vh] mx-auto max-w-[16rem] ${cabinetError !== '' || cabinet.logo === '' ? 'hidden' : ''}`} />
          <span className='text-center font-bold text-red-500 text-2xl'>Simulation de connexion</span>

          <form onSubmit={handleFormSubmit} className={`border border-black rounded-md p-12 mt-4 lg:m-12 pb-0 px-3 pt-2 lg:h-2/3 grow ${cabinetError !== '' ? 'hidden' : ''}`} style={{ width: '-webkit-fill-available' }}>
            <div className='flex flex-col items-start grow' style={{ height: 'calc(100% - 2rem)' }}>
              <img src="/images/connexionIcon.png" alt="User icon" className="mx-auto mb-2 lg:w-1/5 w-10" />

              <label htmlFor="admin-login" className="text-gray-500 font-bold hidden lg:inline-block">Login administrateur :</label>
              <input type="text" id="admin-login" placeholder='Login' value={login} onChange={(e) => setLogin(e.target.value)}
                className="border border-gray-300 bg-white py-2 px-2 rounded-md h-10 mb-3 shadow-bottom" style={{ width: '-webkit-fill-available' }} />

              <label htmlFor="admin-password" className="text-gray-500 font-bold hidden lg:inline-block">Mot de passe :</label>
              <div className="border mb-1 lg:mb-3 flex items-center rounded-md w-full focus-within:ring-2 focus-within:ring-black">
                <input type={`${isPwdShown ? 'text' : 'password'}`} id="admin-password" placeholder='Mot de passe' value={password} onChange={(e) => setPassword(e.target.value)}
                  className="bg-white py-2 px-2 rounded-md h-10 shadow-bottom outline-none w-[inherit]" />
                <button type='button' onClick={() => setIsPwdShown(!isPwdShown)} className="border-l border-l-gray-300 h-10 w-[4rem]">
                  <FontAwesomeIcon className={`h-7 w-7 mx-3`} icon={isPwdShown ? faEyeSlash : faEye} />
                </button>
              </div>

              <label htmlFor="customer-email" className="text-gray-500 font-bold hidden lg:inline-block">Email du client :</label>
              <input type="email" id="email" placeholder='Email' value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)}
                className={`border border-gray-300 bg-white py-2 px-2 rounded-md h-10 mb-3 ${validateEmail(customerEmail) ? '' : 'border-red-500'} shadow-bottom`}
                title="Veuillez entrer une adresse email valide." style={{ width: '-webkit-fill-available' }} />

              {errorMessage && <p className="text-red-500 mx-auto font-bold ">{errorMessage}</p>}

              <button className={`text-white mt-4 mb-3 text-1xl font-bold py-2 px-12 w-52 rounded-lg shadow-md mx-auto`} style={{ backgroundColor: cabinet.color }} type="submit">
                Connexion
              </button>
            </div>

            <PoweredByBc className={'h-8 py-1 mx-auto'} imgClassName={'h-full w-auto m-auto mb-8'} />

          </form>
        </div>
      </div>
    );
  }


  return (
    <div className='h-full w-full'>
      {isMobileScreen ? renderMobileForm() : renderComputerForm()}
    </div>
  );
};